import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { apiPermits, API } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { PERMIT_TYPES, statusLabel, ROLE_LABELS } from '@/constants/permits';
import { PERMIT_DETAIL } from '@/constants/testIds';
import SignaturePad from '@/components/SignaturePad';
import { SignatureEngagementText, EmergencyBanner } from '@/components/AdtExtras';
import {
  ArrowLeft, FileDown, Copy, Trash2, Send, CheckCircle2, XCircle,
  ClipboardCheck, Lock, History as HistoryIcon, Building2, MapPin, Users, Calendar, Tag, AlertOctagon, Shield, Pencil, CornerUpLeft
} from 'lucide-react';

export default function PermitDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('return');
  const { user } = useAuth();
  const [permit, setPermit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [signRole, setSignRole] = useState('Demandeur');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiPermits.get(id);
      setPermit(data);
    } catch {
      toast.error('Permis introuvable');
      navigate('/permits');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [id]);

  const handleDownloadPdf = async () => {
    try {
      const { data } = await apiPermits.pdfBlob(permit.id);
      const blob = new Blob([data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      // Ouvre dans un nouvel onglet sans déclencher les filtres d'extensions
      const w = window.open(url, '_blank', 'noopener,noreferrer');
      if (!w) {
        // Fallback: téléchargement direct
        const a = document.createElement('a');
        a.href = url;
        a.download = `${permit.numero || permit.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      // Libère l'URL après quelques secondes (laisse au navigateur le temps de l'utiliser)
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Impossible de générer le PDF');
    }
  };

  if (loading || !permit) {
    return <div className="text-stone-500 text-sm">Chargement...</div>;
  }

  const typeInfo = PERMIT_TYPES.find((t) => t.id === permit.type) || {};
  const isOwner = permit.created_by === user?.user_id;
  const canEdit = (permit.status === 'brouillon' || permit.status === 'rejete') && isOwner;
  const isReviseur = user?.role === 'reviseur' || user?.role === 'approbateur' || user?.role === 'admin';
  const isApprobateur = user?.role === 'approbateur' || user?.role === 'admin';

  const doStatus = async (status, message) => {
    setActionLoading(true);
    try {
      const { data } = await apiPermits.changeStatus(permit.id, status);
      setPermit(data);
      toast.success(message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  const doSign = async (sigData) => {
    setActionLoading(true);
    try {
      const { data } = await apiPermits.sign(permit.id, signRole, sigData);
      setPermit(data);
      toast.success('Signature ajoutée');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setActionLoading(false); }
  };

  const doDuplicate = async () => {
    try {
      const { data } = await apiPermits.duplicate(permit.id);
      toast.success('Permis dupliqué');
      navigate(`/permits/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const doDelete = async () => {
    if (!window.confirm('Confirmer la suppression de ce brouillon ?')) return;
    try {
      await apiPermits.remove(permit.id);
      toast.success('Supprimé');
      navigate('/permits');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div data-testid={PERMIT_DETAIL.container} className="max-w-7xl space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button onClick={() => navigate('/permits')} className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-stone-600 hover:text-[#09090B]">
          <ArrowLeft className="w-4 h-4" /> Retour à la liste
        </button>
        {(returnTo || permit.parent_id) && (
          <button
            data-testid="permit-detail-back-to-at"
            onClick={() => navigate(returnTo || `/permits/new?edit=${permit.parent_id}`)}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#DA291C] text-white text-xs font-bold uppercase tracking-wider hover:bg-red-700 transition-colors"
          >
            <CornerUpLeft className="w-4 h-4" /> Retour à l'Autorisation de Travail
          </button>
        )}
      </div>

      {/* Header */}
      <div className="section-card">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className={`inline-block text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 ${typeInfo.color || ''} border`}>
              {typeInfo.short || permit.type}
            </div>
            <h1 className="text-3xl md:text-4xl font-display font-black tracking-tighter text-[#09090B] mt-3">
              {permit.titre}
            </h1>
            <div className="flex items-center gap-4 mt-3 text-sm text-stone-600">
              <span data-testid={PERMIT_DETAIL.numero} className="font-mono text-xs bg-stone-100 px-2 py-0.5">
                {permit.numero}
              </span>
              <span data-testid={PERMIT_DETAIL.status} className={`badge-status badge-${permit.status}`}>
                {statusLabel(permit.status)}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid={PERMIT_DETAIL.pdfButton}
              onClick={handleDownloadPdf}
              className="btn-industrial-secondary flex items-center gap-2"
            >
              <FileDown className="w-4 h-4" /> PDF
            </button>
            {canEdit && (
              <button
                data-testid="permit-edit-btn"
                onClick={() => navigate(`/permits/new?edit=${permit.id}`)}
                className="btn-industrial-primary flex items-center gap-2"
              >
                <Pencil className="w-4 h-4" /> Modifier
              </button>
            )}
            <button
              data-testid="briefing-mode-btn"
              onClick={() => navigate(`/permits/${permit.id}/briefing`)}
              className="btn-industrial-secondary flex items-center gap-2"
            >
              <Users className="w-4 h-4" /> Briefing terrain
            </button>
            {permit.type === 'autorisation_travail' && (
              <button
                data-testid="notify-donneur-ordre"
                onClick={async () => {
                  try {
                    const { data } = await apiPermits.notifyDonneurOrdre(permit.id);
                    toast.success(data.ok ? `Email envoyé → ${data.sent_to.join(', ')}` : `Mock email (RESEND_API_KEY manquante) — ${data.sent_to.join(', ')}`);
                    load();
                  } catch (e) {
                    toast.error('Erreur notification');
                  }
                }}
                className="btn-industrial-secondary flex items-center gap-2"
              >
                ✉ Alerter donneur d'ordre
              </button>
            )}
            <button
              data-testid={PERMIT_DETAIL.duplicateButton}
              onClick={doDuplicate}
              className="btn-industrial-secondary flex items-center gap-2"
            >
              <Copy className="w-4 h-4" /> Dupliquer
            </button>
            {canEdit && (
              <button
                data-testid={PERMIT_DETAIL.deleteButton}
                onClick={doDelete}
                className="btn-industrial-secondary flex items-center gap-2 !border-[#DA291C] !text-[#DA291C] hover:!bg-[#DA291C] hover:!text-white"
              >
                <Trash2 className="w-4 h-4" /> Supprimer
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Workflow Actions */}
      <div className="section-card bg-stone-50">
        <div className="label-industrial mb-3">Actions Workflow</div>
        <div className="flex flex-wrap gap-3">
          {permit.status === 'brouillon' && isOwner && (
            <button data-testid={PERMIT_DETAIL.submitButton}
              disabled={actionLoading}
              onClick={() => doStatus('soumis', 'Permis soumis')}
              className="btn-industrial-primary flex items-center gap-2">
              <Send className="w-4 h-4" /> Soumettre pour révision
            </button>
          )}
          {permit.status === 'soumis' && isReviseur && (
            <>
              <button data-testid={PERMIT_DETAIL.reviseButton}
                disabled={actionLoading}
                onClick={() => doStatus('revise', 'Permis révisé')}
                className="btn-industrial-primary flex items-center gap-2">
                <ClipboardCheck className="w-4 h-4" /> Marquer révisé
              </button>
              <button data-testid={PERMIT_DETAIL.rejectButton}
                disabled={actionLoading}
                onClick={() => doStatus('rejete', 'Permis rejeté')}
                className="btn-industrial-secondary flex items-center gap-2 !border-[#DA291C] !text-[#DA291C]">
                <XCircle className="w-4 h-4" /> Rejeter
              </button>
            </>
          )}
          {permit.status === 'revise' && isApprobateur && (
            <>
              <button data-testid={PERMIT_DETAIL.approveButton}
                disabled={actionLoading}
                onClick={() => doStatus('approuve', 'Permis approuvé')}
                className="btn-industrial-primary flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> Approuver
              </button>
              <button data-testid={PERMIT_DETAIL.rejectButton}
                disabled={actionLoading}
                onClick={() => doStatus('rejete', 'Permis rejeté')}
                className="btn-industrial-secondary flex items-center gap-2 !border-[#DA291C] !text-[#DA291C]">
                <XCircle className="w-4 h-4" /> Rejeter
              </button>
            </>
          )}
          {permit.status === 'approuve' && isApprobateur && (
            <button data-testid={PERMIT_DETAIL.closeButton}
              disabled={actionLoading}
              onClick={() => doStatus('cloture', 'Permis clôturé')}
              className="btn-industrial-primary flex items-center gap-2">
              <Lock className="w-4 h-4" /> Clôturer
            </button>
          )}
          {permit.status === 'rejete' && isOwner && (
            <button
              disabled={actionLoading}
              onClick={() => doStatus('brouillon', 'Remis en brouillon')}
              className="btn-industrial-secondary"
            >
              Remettre en brouillon
            </button>
          )}
          {permit.status === 'cloture' && (
            <div className="text-sm text-stone-600 italic">Permis clôturé — Aucune action disponible.</div>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Identification */}
          <div className="section-card">
            <h2 className="text-xl font-display font-bold mb-4">Identification</h2>
            <dl className="grid sm:grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="label-industrial flex items-center gap-1"><Building2 className="w-3 h-3" /> Site</dt>
                <dd className="font-semibold text-[#09090B]">{permit.site_code}</dd>
              </div>
              <div>
                <dt className="label-industrial flex items-center gap-1"><Tag className="w-3 h-3" /> Type</dt>
                <dd className="font-semibold text-[#09090B]">{typeInfo.label || permit.type}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="label-industrial flex items-center gap-1"><MapPin className="w-3 h-3" /> Lieu</dt>
                <dd className="font-semibold text-[#09090B]">{permit.lieu}</dd>
              </div>
              <div>
                <dt className="label-industrial flex items-center gap-1"><Calendar className="w-3 h-3" /> Début</dt>
                <dd className="font-semibold text-[#09090B]">{permit.date_debut}</dd>
              </div>
              <div>
                <dt className="label-industrial flex items-center gap-1"><Calendar className="w-3 h-3" /> Fin</dt>
                <dd className="font-semibold text-[#09090B]">{permit.date_fin}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="label-industrial flex items-center gap-1"><Users className="w-3 h-3" /> Entreprise</dt>
                <dd className="font-semibold text-[#09090B]">{permit.entreprise_exterieure || 'Travaux internes'}</dd>
              </div>
              {permit.description && (
                <div className="sm:col-span-2">
                  <dt className="label-industrial">Description</dt>
                  <dd className="text-stone-800 leading-relaxed whitespace-pre-wrap">{permit.description}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* HSE */}
          <div className="section-card">
            <h2 className="text-xl font-display font-bold mb-4 flex items-center gap-2">
              <AlertOctagon className="w-5 h-5 text-[#DA291C]" /> Analyse HSE
            </h2>
            {permit.type === 'autorisation_travail' && Array.isArray(permit.extra?.sop_steps) && permit.extra.sop_steps.length > 0 ? (
              <div className="overflow-x-auto mb-4">
                <table className="w-full text-xs border border-stone-200">
                  <thead className="bg-stone-50 border-b-2">
                    <tr>
                      <th className="text-left p-2 w-20 font-bold uppercase text-[10px]">Étape</th>
                      <th className="text-left p-2 font-bold uppercase text-[10px]">D1 — Sous-étapes (SOP)</th>
                      <th className="text-left p-2 font-bold uppercase text-[10px]">D2 — Risques & mesures de prévention</th>
                    </tr>
                  </thead>
                  <tbody>
                    {permit.extra.sop_steps.map((s, i) => (
                      <tr key={i} className="border-b border-stone-100 align-top">
                        <td className="p-2 font-bold bg-stone-50">Étape {i + 1}</td>
                        <td className="p-2 whitespace-pre-wrap text-stone-800">{s.sous_etapes || '—'}</td>
                        <td className="p-2 whitespace-pre-wrap text-stone-800">{s.risques_mesures || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
            <div className="grid sm:grid-cols-3 gap-6">
              {permit.type !== 'autorisation_travail' && (
                <>
                  <div>
                    <div className="label-industrial mb-2">Risques</div>
                    <ul className="space-y-1.5">
                      {permit.risques_identifies?.length ? permit.risques_identifies.map((r, i) => (
                        <li key={i} className="text-sm text-stone-800 flex gap-2"><span className="text-[#DA291C]">▸</span>{r}</li>
                      )) : <li className="text-xs text-stone-400 italic">Aucun</li>}
                    </ul>
                  </div>
                  <div>
                    <div className="label-industrial mb-2">Mesures</div>
                    <ul className="space-y-1.5">
                      {permit.mesures_prevention?.length ? permit.mesures_prevention.map((r, i) => (
                        <li key={i} className="text-sm text-stone-800 flex gap-2"><Shield className="w-3 h-3 text-green-700 mt-1 flex-shrink-0" />{r}</li>
                      )) : <li className="text-xs text-stone-400 italic">Aucune</li>}
                    </ul>
                  </div>
                </>
              )}
              <div className={permit.type === 'autorisation_travail' ? 'sm:col-span-3' : ''}>
                <div className="label-industrial mb-2">EPI</div>
                <ul className="space-y-1.5">
                  {permit.epi_requis?.length ? permit.epi_requis.map((r, i) => (
                    <li key={i} className="text-sm text-stone-800 flex gap-2"><span className="text-[#09090B]">●</span>{r}</li>
                  )) : <li className="text-xs text-stone-400 italic">Aucun</li>}
                </ul>
                {permit.type === 'autorisation_travail' && permit.extra?.adt_epi && (
                  <div className="mt-3 text-xs text-stone-700">
                    <div className="font-bold uppercase tracking-wider text-stone-500 mb-1">Q — EPI Spécifiques sélectionnés</div>
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(permit.extra.adt_epi).filter(([, v]) => v).map(([k]) => (
                        <span key={k} className="bg-stone-100 border border-stone-300 px-2 py-0.5 text-[10px]">{k.replace('q_', '').replace(/_/g, ' ')}</span>
                      ))}
                      {Object.values(permit.extra.adt_epi).filter(Boolean).length === 0 && (
                        <span className="italic text-stone-400">Aucun EPI spécifique sélectionné</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Extra fields */}
          {permit.extra && Object.keys(permit.extra).length > 0 && (
            <div className="section-card">
              <h2 className="text-xl font-display font-bold mb-4">Spécificités</h2>
              {permit.type === 'bon_isolation_energies' && permit.extra.bie_mode === 'manuel' && Array.isArray(permit.extra.lines) && (
                <div className="overflow-x-auto mb-4">
                  <table className="w-full text-xs border border-stone-200">
                    <thead className="bg-stone-50">
                      <tr>
                        <th className="text-left p-2 border-b">#</th>
                        <th className="text-left p-2 border-b">Équipement</th>
                        <th className="text-left p-2 border-b">Dispositif d'isolement</th>
                        <th className="text-left p-2 border-b">Énergie</th>
                        <th className="text-left p-2 border-b">État à zéro (TO)</th>
                        <th className="text-left p-2 border-b">TEST-OUT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {permit.extra.lines.map((l, i) => (
                        <tr key={i} className="border-b border-stone-100">
                          <td className="p-2 font-mono text-stone-500">{i + 1}</td>
                          <td className="p-2">{l.equipement || '—'}</td>
                          <td className="p-2">{l.dispositif_isolement || '—'}</td>
                          <td className="p-2">{l.energie || '—'}</td>
                          <td className="p-2">{l.etat_to || '—'}</td>
                          <td className="p-2">{l.test_out || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <dl className="grid sm:grid-cols-2 gap-3 text-sm">
                {Object.entries(permit.extra).filter(([k, v]) => {
                  if (k === 'lines' || k === 'sop_steps' || k === 'risk_blocks' || k === 'bloc_w' ||
                      k === 'organigramme' || k === 'interferences' || k === 'adt_epi') return false;
                  if (v === null || v === undefined) return false;
                  if (typeof v === 'object') return false;
                  return true;
                }).map(([k, v]) => (
                  <div key={k}>
                    <dt className="label-industrial">{k.replace(/_/g, ' ')}</dt>
                    <dd className="font-semibold text-[#09090B]">{typeof v === 'boolean' ? (v ? 'OUI' : 'NON') : String(v)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {/* Signatures */}
          <div className="section-card">
            <h2 className="text-xl font-display font-bold mb-4">Signatures</h2>
            <SignatureEngagementText />
            {permit.signatures?.length > 0 && (
              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                {permit.signatures.map((s, i) => (
                  <div key={i} className="border border-stone-200 p-3">
                    <div className="label-industrial">{s.signer_role}</div>
                    <div className="font-semibold text-[#09090B] text-sm mt-1">{s.signer_name}</div>
                    <div className="text-xs text-stone-500">{(s.signed_at || '').slice(0, 16).replace('T', ' ')}</div>
                    {s.signature_data && (
                      <img src={s.signature_data} alt="signature" className="mt-2 h-16 bg-white border border-stone-100" />
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="border-t border-stone-200 pt-4">
              <label className="label-industrial">Ajouter une signature — Mon rôle</label>
              <select
                data-testid={PERMIT_DETAIL.signatureRole}
                value={signRole}
                onChange={(e) => setSignRole(e.target.value)}
                className="input-industrial w-full max-w-xs mb-4"
              >
                <option>Demandeur</option>
                <option>Réviseur HSE</option>
                <option>Approbateur Site</option>
                <option>Chef de chantier</option>
                <option>Intervenant</option>
              </select>
              <SignaturePad onSave={doSign} role={signRole} />
            </div>
          </div>
        </div>

        {/* Right: history */}
        <div className="space-y-6">
          <div className="section-card">
            <h2 className="text-lg font-display font-bold mb-4 flex items-center gap-2">
              <HistoryIcon className="w-4 h-4" /> Historique
            </h2>
            <ol className="space-y-4">
              {(permit.history || []).slice().reverse().map((h, i) => (
                <li key={i} className="border-l-2 border-stone-200 pl-4 relative">
                  <span className="absolute -left-1.5 top-1 w-2.5 h-2.5 bg-[#DA291C] rounded-full"></span>
                  <div className="text-xs text-stone-500">{(h.at || '').slice(0, 16).replace('T', ' ')}</div>
                  <div className="text-sm font-semibold text-[#09090B] mt-0.5">{h.action}</div>
                  <div className="text-xs text-stone-600">{h.by}</div>
                  {h.commentaire && <div className="text-xs text-stone-500 italic mt-1">"{h.commentaire}"</div>}
                </li>
              ))}
            </ol>
          </div>

          <div className="section-card">
            <h2 className="text-lg font-display font-bold mb-3">Méta</h2>
            <dl className="text-xs space-y-2">
              <div className="flex justify-between">
                <dt className="text-stone-500 uppercase tracking-wider">Créé par</dt>
                <dd className="font-semibold text-[#09090B]">{permit.created_by_name}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-stone-500 uppercase tracking-wider">Créé le</dt>
                <dd className="font-semibold text-[#09090B]">{(permit.created_at || '').slice(0, 16).replace('T', ' ')}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-stone-500 uppercase tracking-wider">MAJ</dt>
                <dd className="font-semibold text-[#09090B]">{(permit.updated_at || '').slice(0, 16).replace('T', ' ')}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}
